import { useQuery } from '@tanstack/react-query'
import { getMyPlanning } from '../../../shared/api/planning.api'
import { sumMoney } from '../../planning/planningAmount'
import { formatPlanRub, formatRub, monthLabel, percentText, progressColor } from './planFactFormat'

/**
 * Блок «План и факт» вверху дашборда продажника.
 *
 * Менеджер заходит каждый день и сразу видит, на каком он этапе выполнения
 * месячного плана: прогресс-бар факт/план + расчётные КП выставлено / в заказ
 * и АКБ. План/факт задаёт админ или РОП в разделе «Планирование».
 */
export function ManagerPlanFactCard() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['my-planning'],
    queryFn: getMyPlanning,
    staleTime: 60_000,
  })

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="h-20 animate-pulse bg-gray-100 rounded-xl" />
      </div>
    )
  }
  if (isError || !data) return null

  const { planAmount, factAmount, quotedAmount, orderedAmount, akbPlanCount, akbFactCount } = data
  const ratio = planAmount > 0 ? factAmount / planAmount : 0
  const widthPct = Math.min(100, Math.round(ratio * 100))
  const remaining = Math.max(0, sumMoney([planAmount, -factAmount]))

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-gray-900">
          План и факт · <span className="capitalize">{monthLabel(data.periodMonth)}</span>
        </h2>
        <span className="text-2xl font-bold tabular-nums text-gray-900">
          {percentText(planAmount, factAmount)}
        </span>
      </div>

      {planAmount <= 0 ? (
        <p className="mt-3 text-sm text-gray-400">
          План на этот месяц ещё не задан — его выставляет руководитель в разделе «Планирование».
        </p>
      ) : (
        <>
          {/* Прогресс-бар факт/план */}
          <div className="mt-3 h-3 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${progressColor(ratio)}`}
              style={{ width: `${widthPct}%` }}
            />
          </div>
          <div className="mt-1.5 flex justify-between text-xs text-gray-500 tabular-nums">
            <span>Факт: {formatPlanRub(factAmount)}</span>
            <span>План: {formatPlanRub(planAmount)}</span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            До выполнения плана осталось{' '}
            <span className="font-semibold text-gray-700">{formatPlanRub(remaining)}</span>
          </p>
        </>
      )}

      {/* Расчётные показатели за месяц */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Metric label="КП выставлено" value={formatRub(quotedAmount)} />
        <Metric label="В заказ" value={formatRub(orderedAmount)} />
        <Metric
          label="АКБ (клиентов)"
          value={akbPlanCount > 0 ? `${akbFactCount} из ${akbPlanCount}` : String(akbFactCount)}
        />
      </div>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-gray-50 px-3 py-2">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-900 tabular-nums">{value}</div>
    </div>
  )
}
